import { API_URL } from "../config/api";

/* The access token, and the one place that renews it.

   WHY THERE IS STILL A localStorage COPY. The refresh credential lives in an
   httpOnly cookie and is never visible to this file - that is the part that
   matters. The access token is mirrored to localStorage only so a reload can
   carry on when the browser refuses to send that cookie at all: the frontend
   and the API are on different domains, and Safari's ITP drops cross-site
   cookies outright. Without the mirror those users are signed out by every
   page refresh. What changed is the size of the exposure: the token being
   written here now lasts fifteen minutes rather than seven days, so an XSS
   read of it buys minutes, not a week, and it can no longer be used to mint
   new sessions - only the cookie can do that.

   Everything else in the app goes through getAccessToken/refreshAccessToken
   rather than reading the key itself, so this trade-off has exactly one
   site and can be revisited in one place. */

const STORAGE_KEY = "token";

/* The live value. Memory is the source of truth within a tab so a refresh
   is visible to every in-flight caller immediately, without a storage read. */
let accessToken: string | null = null;

/* Concurrent 401s must produce ONE refresh, not one each.

   A dashboard that fires six requests on mount will see six 401s the moment
   the token lapses. Six refreshes would rotate the token six times, and with
   rotation the second call presents a token the first already replaced -
   which the server correctly reads as a replay and revokes the whole family.
   Un-deduplicated refresh does not merely waste calls here; it logs the user
   out. Every caller therefore awaits the same promise. */
let inFlight: Promise<string | null> | null = null;

const readMirror = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    /* Storage can throw in private modes and with cookies disabled. A
       session held only in memory is still a working session. */
    return null;
  }
};

const writeMirror = (token: string | null): void => {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Ignored for the same reason. */
  }
};

export const getAccessToken = (): string | null => {
  if (accessToken) return accessToken;
  accessToken = readMirror();
  return accessToken;
};

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
  writeMirror(token);
};

export const clearAccessToken = (): void => setAccessToken(null);

/* Ask the cookie for a new access token.

   Returns the token, or null if there is no live session - callers treat
   null as "genuinely signed out" rather than as a transport failure, because
   the endpoint answers 401 for both an absent and a revoked session. */
export const refreshAccessToken = async (): Promise<string | null> => {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        /* The refresh cookie is the entire credential; no Authorization
           header is sent, because the access token is expired by definition
           at this point. */
        credentials: "include",
      });

      if (!response.ok) {
        clearAccessToken();
        return null;
      }

      const data = await response.json();
      if (!data?.token) {
        clearAccessToken();
        return null;
      }

      setAccessToken(data.token);
      return data.token as string;
    } catch {
      /* A network failure is not a signed-out user: the token is left alone
         so a later call can retry rather than tearing down the session over
         one dropped request. */
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};
